/**
 * Stage 6C: Shared Onboarding Wizard Business Logic & State Derivation
 *
 * Pure TypeScript functions shared by OnboardingWizard component and focused tests:
 * - Step progression determination based on backend onboarding_status.
 * - Truthful setup status derivation based on actual backend fields (verified_at, tls_status, etc.).
 * - Lifecycle manager for coordinating in-flight requests, abort signals, and delayed transitions.
 */

import type { WebsiteRecord } from "../../types/telemetry.ts";
import { formatDateTimeIST } from "../../utils/dateTime.ts";

export type OnboardingStep = "DETAILS" | "VERIFY_TXT" | "CONNECT_DNS" | "SETUP_STATUS";

export interface StatusItem {
  status: string;
  badge: "verified" | "warning" | "pending" | "error";
  description: string;
}

export interface SetupStatusSummary {
  ownership: StatusItem;
  routing: StatusItem;
  tls: StatusItem;
  traffic: StatusItem;
  banner: {
    title: string;
    message: string;
    type: "ready" | "pending" | "active" | "warning" | "error";
  };
  isDevFixture: boolean;
}

/**
 * Determines the starting step from the backend website record.
 * Handles DRAFT, PENDING_OWNERSHIP, OWNERSHIP_VERIFIED, ROUTING_READY,
 * PROVISIONING_TLS, ACTIVE, SUSPENDED, and DEACTIVATED explicitly.
 */
export function determineInitialStep(website?: WebsiteRecord | null): OnboardingStep {
  if (!website) return "DETAILS";
  switch (website.onboarding_status) {
    case "DRAFT":
    case "PENDING_OWNERSHIP":
      return "VERIFY_TXT";
    case "OWNERSHIP_VERIFIED":
      return "CONNECT_DNS";
    case "ROUTING_READY":
    case "PROVISIONING_TLS":
    case "ACTIVE":
    case "SUSPENDED":
    case "DEACTIVATED":
      return "SETUP_STATUS";
    default:
      return "VERIFY_TXT";
  }
}

/**
 * Truthfully derives setup status indicators from actual backend fields.
 *
 * Invariants:
 * - ACTIVE websites must not appear inactive.
 * - Issued certificates must not appear pending.
 * - Handles TLS PENDING_CHALLENGE, not the unsupported PENDING value.
 * - Displays NOT_REQUIRED accurately for local development websites, without implying a missing certificate.
 * - Handles onboarding PROVISIONING_TLS, SUSPENDED, and DEACTIVATED explicitly.
 * - Do not infer fresh DNS verification merely from ACTIVE status, particularly for development fixtures.
 * - Removes false promises of automated TLS scheduling; explains HTTPS and activation are not yet available.
 * - Uses existing formatDateTimeIST for verification timestamps.
 */
export function deriveSetupStatus(website: WebsiteRecord): SetupStatusSummary {
  const isDevFixture = Boolean(website.is_development);
  const isOwnershipVerified = Boolean(
    website.verified_at ||
      (website.onboarding_status === "OWNERSHIP_VERIFIED" ||
        website.onboarding_status === "ROUTING_READY" ||
        website.onboarding_status === "PROVISIONING_TLS" ||
        ((website.onboarding_status === "ACTIVE" ||
          website.onboarding_status === "SUSPENDED" ||
          website.onboarding_status === "DEACTIVATED") &&
          !isDevFixture))
  );

  // 1. Ownership Verification
  let ownership: StatusItem;
  if (website.verified_at) {
    ownership = {
      status: "VERIFIED",
      badge: "verified",
      description: `DNS TXT challenge verified on ${formatDateTimeIST(website.verified_at)}.`,
    };
  } else if (isOwnershipVerified) {
    ownership = {
      status: "VERIFIED",
      badge: "verified",
      description: "Domain ownership verified via DNS TXT challenge.",
    };
  } else if (isDevFixture) {
    ownership = {
      status: "BYPASSED (DEV)",
      badge: "warning",
      description: "Development fixture seeded without authoritative DNS challenge.",
    };
  } else {
    ownership = {
      status: "UNVERIFIED",
      badge: "pending",
      description: "Domain ownership challenge has not been verified.",
    };
  }

  // 2. DNS Routing Readiness
  let routing: StatusItem;
  if (website.onboarding_status === "ACTIVE") {
    if (isDevFixture) {
      routing = {
        status: "ROUTING ACTIVE (DEV)",
        badge: "warning",
        description: "Development proxy routing active; live CNAME record not evaluated.",
      };
    } else {
      routing = {
        status: "ROUTING ACTIVE",
        badge: "verified",
        description: "Traffic is actively routed through AIShield ingress.",
      };
    }
  } else if (
    website.onboarding_status === "ROUTING_READY" ||
    website.onboarding_status === "PROVISIONING_TLS"
  ) {
    routing = {
      status: "ROUTING READY",
      badge: "verified",
      description: "CNAME resolves to configured AIShield ingress target.",
    };
  } else if (website.onboarding_status === "SUSPENDED") {
    routing = {
      status: "SUSPENDED",
      badge: "warning",
      description: "DNS routing is suspended.",
    };
  } else if (website.onboarding_status === "DEACTIVATED") {
    routing = {
      status: "DEACTIVATED",
      badge: "error",
      description: "DNS routing is deactivated.",
    };
  } else {
    routing = {
      status: "PENDING",
      badge: "pending",
      description: "CNAME routing verification is pending.",
    };
  }

  // 3. HTTPS Certificate Readiness
  let tls: StatusItem;
  const isTlsNotRequired =
    website.tls_status === "NOT_REQUIRED" ||
    (isDevFixture && (website.tls_status === "NOT_PROVISIONED" || !website.tls_status));

  if (website.tls_status === "PROVISIONED") {
    tls = {
      status: "PROVISIONED",
      badge: "verified",
      description: "TLS certificate is active and provisioned.",
    };
  } else if (website.tls_status === "PENDING_CHALLENGE") {
    tls = {
      status: "PENDING CHALLENGE",
      badge: "pending",
      description: "ACME TLS challenge validation in progress.",
    };
  } else if (isTlsNotRequired) {
    tls = {
      status: "NOT REQUIRED",
      badge: "verified",
      description: "TLS certificate is not required for local development HTTP origins.",
    };
  } else if (website.tls_status === "FAILED") {
    tls = {
      status: "FAILED",
      badge: "error",
      description: "TLS certificate provisioning failed.",
    };
  } else {
    tls = {
      status: "NOT PROVISIONED",
      badge: "pending",
      description: "HTTPS certificate is not yet provisioned. Automated TLS issuance is not yet available.",
    };
  }

  // 4. Traffic Protection Activation
  let traffic: StatusItem;
  if (website.onboarding_status === "ACTIVE") {
    traffic = {
      status: "ACTIVE",
      badge: "verified",
      description: "Inbound traffic is protected by AIShield proxy.",
    };
  } else if (website.onboarding_status === "SUSPENDED") {
    traffic = {
      status: "SUSPENDED",
      badge: "warning",
      description: "Traffic protection is suspended.",
    };
  } else if (website.onboarding_status === "DEACTIVATED") {
    traffic = {
      status: "DEACTIVATED",
      badge: "error",
      description: "Traffic protection is deactivated.",
    };
  } else if (website.onboarding_status === "PROVISIONING_TLS") {
    traffic = {
      status: "INACTIVE",
      badge: "pending",
      description: "Traffic protection is inactive while TLS provisioning completes.",
    };
  } else {
    traffic = {
      status: "INACTIVE",
      badge: "pending",
      description: "Traffic protection is inactive. Live traffic activation is not yet available.",
    };
  }

  // 5. Summary Banner - must agree with individual status cards
  let banner: SetupStatusSummary["banner"];
  if (website.onboarding_status === "SUSPENDED") {
    banner = {
      title: "Website suspended",
      message: "Traffic protection and routing for this website have been suspended.",
      type: "warning",
    };
  } else if (website.onboarding_status === "DEACTIVATED") {
    banner = {
      title: "Website deactivated",
      message: "This website has been deactivated. Inbound traffic is not protected.",
      type: "error",
    };
  } else if (website.onboarding_status === "ACTIVE") {
    if (website.tls_status === "PROVISIONED" || isTlsNotRequired) {
      banner = {
        title: "Website active and protected",
        message: "Traffic routing and security inspection are fully operational. You can monitor live telemetry in the dashboard.",
        type: "active",
      };
    } else {
      banner = {
        title: "Website active — TLS pending",
        message: "Traffic routing is active, but TLS certificate provisioning has not completed.",
        type: "pending",
      };
    }
  } else if (website.onboarding_status === "PROVISIONING_TLS") {
    banner = {
      title: "TLS provisioning in progress",
      message: "DNS routing is confirmed. TLS certificate issuance is currently in progress before traffic activation.",
      type: "pending",
    };
  } else if (website.onboarding_status === "ROUTING_READY") {
    banner = {
      title: "DNS routing confirmed — activation pending",
      message: "DNS routing verification is complete. HTTPS certificate provisioning and live traffic activation are not yet available and remain pending backend deployment.",
      type: "ready",
    };
  } else {
    banner = {
      title: "Setup in progress",
      message: "Complete the remaining verification steps before traffic can be protected.",
      type: "pending",
    };
  }

  return {
    ownership,
    routing,
    tls,
    traffic,
    banner,
    isDevFixture,
  };
}

/**
 * Shared lifecycle manager coordinating abort signals and delayed transitions.
 * Used directly by OnboardingWizard component and tested in focused unit tests.
 */
export class WizardLifecycleManager {
  private abortController: AbortController | null = null;
  private transitionTimer: ReturnType<typeof setTimeout> | null = null;
  private onStepTransition?: (step: OnboardingStep) => void;

  constructor(onStepTransition?: (step: OnboardingStep) => void) {
    this.onStepTransition = onStepTransition;
  }

  /**
   * Returns a fresh, non-aborted AbortSignal for initiating an asynchronous operation.
   * Cancels any previously active in-flight signal before creating a new one.
   */
  public createSignal(): AbortSignal {
    if (this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort(new Error("Superseded by new operation"));
    }
    this.abortController = new AbortController();
    return this.abortController.signal;
  }

  /**
   * Cancels all pending work: aborts active request signal and clears transition timer.
   */
  public cancelPendingWork(reason = "Wizard cancelled"): void {
    if (this.abortController && !this.abortController.signal.aborted) {
      this.abortController.abort(new Error(reason));
      this.abortController = null;
    }
    this.clearStepTransition();
  }

  /**
   * Schedules a delayed step transition, guaranteed to be aborted if cancelPendingWork is called.
   */
  public scheduleStepTransition(targetStep: OnboardingStep, delayMs = 1200): void {
    this.clearStepTransition();
    const activeController = this.abortController;
    this.transitionTimer = setTimeout(() => {
      if (activeController?.signal.aborted) return;
      this.onStepTransition?.(targetStep);
      this.transitionTimer = null;
    }, delayMs);
  }

  /**
   * Clears any active step transition timer.
   */
  public clearStepTransition(): void {
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
  }

  public isTransitionPending(): boolean {
    return this.transitionTimer !== null;
  }

  public isAborted(): boolean {
    return this.abortController ? this.abortController.signal.aborted : false;
  }
}
